import {SsoStep as SsoStepEnum} from 'src/openapi/admin/models/SsoStep';

import googleLogo from './assets/google-logo.svg';
import microsoftLogo from './assets/microsoft-logo.svg';
import oktaLogo from './assets/okta-logo.svg';
import oneloginLogo from './assets/onelogin-logo.svg';
import otherProviderLogo from './assets/other-provider-logo.svg';
import pingfederateLogo from './assets/pingfederate-logo.svg';

export type SsoStep = {
    label: string,
    number: number,
    path: string,
    sessionStorageKey?: string,
    ssoStep: SsoStepEnum,
};

export const SSO_BASE_PATH = '/sso-settings';
export const SETUP_COMPLETE_PATH = 'setup-complete';
export const CONNECTION_PATH = 'connection';

export const SSO_STEPS: SsoStep[] = [
    {label: 'Get Started', number: 1, path: 'get-started', ssoStep: SsoStepEnum.START_STEP},
    {label: 'Select IdP', number: 2, path: 'select-idp', sessionStorageKey: 'selectedProvider', ssoStep: SsoStepEnum.SELECT_IDP},
    {label: 'Verify Domain', number: 3, path: 'verify-domain', sessionStorageKey: 'verifyDomain', ssoStep: SsoStepEnum.VERIFY_DOMAIN},
    {label: 'Download SP Metadata', number: 4, path: 'download-sp-metadata', sessionStorageKey: 'downloadSpMetadata', ssoStep: SsoStepEnum.SP_METADATA},
    {label: 'Provide User Attributes', number: 5, path: 'confirm-login-fields', sessionStorageKey: 'confirmLoginFields', ssoStep: SsoStepEnum.USER_ATTRIBUTES},
    {label: 'Upload IdP Metadata', number: 6, path: 'upload-metadata', sessionStorageKey: 'uploadMetadata', ssoStep: SsoStepEnum.IDP_METADATA},
    {label: 'Test End User', number: 7, path: 'test-end-user', sessionStorageKey: 'testEndUser', ssoStep: SsoStepEnum.TEST_CONNECTION},
    {label: 'Review & Enforce', number: 8, path: 'review-and-enable', sessionStorageKey: 'reviewAndEnable', ssoStep: SsoStepEnum.REVIEW_AND_ENFORCE},
];

export type IdpProvider = {
    id: string,
    name: string,
    description: string,
    logo: string,
};

export const IDP_PROVIDERS: IdpProvider[] = [
    {id: 'okta', name: 'Okta', description: 'Enterprise identity management', logo: oktaLogo},
    {id: 'microsoft', name: 'Microsoft Entra ID', description: 'If your team signs into Outlook or Microsoft 365', logo: microsoftLogo},
    {id: 'onelogin', name: 'OneLogin', description: 'Cloud-based identity management', logo: oneloginLogo},
    {id: 'google', name: 'Google Workspace', description: 'If your team uses Gmail with your company domain', logo: googleLogo},
    {id: 'pingfederate', name: 'PingFederate', description: 'PingIdentity federation server', logo: pingfederateLogo},
    {id: 'other', name: 'Other Provider', description: 'Any other compatible login provider', logo: otherProviderLogo},
];

export const GET_STARTED_CONTENT = {
    subtitle: 'Let your team sign in using your company\'s Identity Provider (IdP) — such as Okta, Microsoft Entra ID, Google Workspace, or OneLogin.',
    whatYouNeed: {
        heading: 'What you\'ll need',
        items: [
            'Admin access to your Identity Provider (IdP)',
            'A verified corporate email domain',
            'Approximately 15\u201320 minutes to complete setup',
        ],
    },
    howItWorks: {
        heading: 'How it works',
        description:
            'This page will guide you through setting up SAML 2.0 based SSO between PitchBook and your identity' +
            ' provider. ' +
            'You\'ll exchange metadata, configure attribute mappings, and test the connection before enforcing SSO for your organization.',
    },
    whatIsSaml: {
        heading: 'What is SAML?',
        description: 'SAML is the secure method that connects PitchBook to your company\'s login system.',
    },
    note: 'Only SAML 2.0 protocol is supported. If you need assistance at any point, contact PitchBook Support.',
    legalExemption: 'Please note that if SSO configuration is performed independently by the client or a third party, the client is solely responsible for the accuracy of the setup, access management, and security configuration. The PitchBook is not liable for any access issues, authentication failures, security incidents, or service disruptions resulting from misconfiguration of SSO settings.',
};
